import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { apiClient } from "@/App";
import { Sunrise, Video, MapPin, CalendarCheck, AlertTriangle, CheckSquare, IndianRupee, Cake, PartyPopper } from "lucide-react";
import { formatCurrency } from "@/lib/format";

const Section = ({ icon: Icon, title, color, count, children, testId }) => (
  <div className="bg-[#141414] border border-[#1A1A1A] rounded-xl p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4" style={{ color }} />
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</p>
      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${color}20`, color }}>{count}</span>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ time, primary, secondary, accent }) => (
  <div className="flex items-center gap-3 text-sm">
    {time && <span className="text-[#FF4500] font-mono text-xs shrink-0 w-12">{time}</span>}
    <span className={`truncate ${accent || "text-gray-200"}`}>{primary}</span>
    {secondary && <span className="text-gray-500 text-xs truncate hidden sm:inline">· {secondary}</span>}
  </div>
);

export const TodayBriefing = () => {
  const [briefing, setBriefing] = useState(null);

  useEffect(() => {
    apiClient.get("/dashboard/today-briefing")
      .then((res) => setBriefing(res.data))
      .catch(() => {});
  }, []);

  if (!briefing) return null;

  const schedule = [
    ...briefing.meetings.map((m) => ({ time: m.scheduled_time, primary: m.agenda || "Meeting", secondary: m.project_name, icon: "meeting" })),
    ...briefing.site_visits.map((v) => ({ time: v.scheduled_time, primary: v.purpose || "Site Visit", secondary: v.project_name, icon: "visit" })),
    ...briefing.appointments.map((a) => ({ time: a.scheduled_time, primary: a.meeting_type || "Appointment", secondary: a.guest_name, icon: "appt" })),
    ...briefing.confirmed_meetings.map((c) => ({ time: c.scheduled_time, primary: c.title, secondary: c.meeting_with, icon: "confirmed" })),
  ].sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const overdueCount = briefing.overdue_tasks.length;
  const dueTodayCount = briefing.tasks_due_today.length;
  const paymentsTotal = briefing.payments_due_week.reduce((s, p) => s + (p.amount || 0), 0);
  const allClear = schedule.length === 0 && overdueCount === 0 && dueTodayCount === 0 && briefing.payments_due_week.length === 0 && briefing.birthdays.length === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="mb-6 sm:mb-8"
      data-testid="today-briefing"
    >
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#141414] via-[#0F0F0F] to-[#141414] border border-[#1A1A1A] p-4 sm:p-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#FF4500]/5 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Sunrise className="w-5 h-5 text-[#FF4500]" />
            <h2 className="text-white font-semibold text-base sm:text-lg">Today's Briefing</h2>
            <span className="text-xs text-gray-500 ml-1 hidden sm:inline">{briefing.day_name}, {briefing.date}</span>
          </div>

          {allClear ? (
            <div className="flex items-center gap-3 py-4" data-testid="briefing-all-clear">
              <PartyPopper className="w-6 h-6 text-[#10B981]" />
              <div>
                <p className="text-white text-sm font-medium">All clear today!</p>
                <p className="text-gray-500 text-xs">No meetings, overdue tasks, or payments due. Enjoy the calm.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {schedule.length > 0 && (
                <Section icon={CalendarCheck} title="Today's Schedule" color="#FF6B35" count={schedule.length} testId="briefing-schedule">
                  {schedule.slice(0, 5).map((s, i) => (
                    <Row key={i} time={s.time} primary={s.primary} secondary={s.secondary} />
                  ))}
                  {schedule.length > 5 && <p className="text-xs text-gray-600">+{schedule.length - 5} more</p>}
                </Section>
              )}

              {(overdueCount > 0 || dueTodayCount > 0) && (
                <Section icon={overdueCount > 0 ? AlertTriangle : CheckSquare} title="Tasks" color={overdueCount > 0 ? "#EF4444" : "#F59E0B"} count={overdueCount + dueTodayCount} testId="briefing-tasks">
                  {briefing.overdue_tasks.slice(0, 3).map((t, i) => (
                    <Row key={`o${i}`} primary={t.title} secondary={`overdue since ${t.due_date}`} accent="text-red-400" />
                  ))}
                  {briefing.tasks_due_today.slice(0, 3).map((t, i) => (
                    <Row key={`d${i}`} primary={t.title} secondary="due today" />
                  ))}
                  <Link to="/tasks" className="text-xs text-[#FF4500] hover:underline inline-block mt-1">View all tasks →</Link>
                </Section>
              )}

              {briefing.payments_due_week.length > 0 && (
                <Section icon={IndianRupee} title="Payments Due This Week" color="#10B981" count={briefing.payments_due_week.length} testId="briefing-payments">
                  {briefing.payments_due_week.slice(0, 4).map((p, i) => (
                    <Row key={i} primary={formatCurrency(p.amount)} secondary={`${p.project_name} · ${p.due_date}`} accent="text-[#10B981]" />
                  ))}
                  <p className="text-xs text-gray-500 mt-1">Total: <span className="text-white">{formatCurrency(paymentsTotal)}</span></p>
                </Section>
              )}

              {briefing.birthdays.length > 0 && (
                <Section icon={Cake} title="Birthdays Today" color="#A78BFA" count={briefing.birthdays.length} testId="briefing-birthdays">
                  {briefing.birthdays.map((b, i) => (
                    <Row key={i} primary={b.name} secondary="wish them well!" accent="text-[#A78BFA]" />
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
